// A janela de escrita: nova, resposta e encaminhamento.
//
// Flutuante no canto, como no mockup — e não uma rota própria — porque escrever
// e consultar a caixa ao mesmo tempo é o gesto mais comum do e-mail: "o que
// mesmo ele pediu?" enquanto a resposta está aberta.

import { useCallback, useEffect, useRef, useState } from "react";

import { Editor, htmlParaTexto, textoParaHTML } from "./Editor";
import { ProgramarEnvio } from "./ProgramarEnvio";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { separarEnderecos } from "./endereco";

import * as conta from "../api/conta";
import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import type { MensagemEnvio } from "../api/tipos";
import { CampoDeEnderecos } from "./CampoDeEnderecos";
import { Icone } from "../ui/Icone";
import { tamanho } from "../ui/formato";

export interface Rascunho {
  modo: "novo" | "responder" | "encaminhar";
  /** A mensagem de origem, quando é resposta ou encaminhamento. */
  origem?: string;
  para?: string;
  cc?: string;
  assunto?: string;
  corpo?: string;
}

const ESPERA_RASCUNHO = 3000;

/** Teto dos anexos, somados, em bytes de ARQUIVO (antes do base64).
 *
 * A corrente inteira depende deste número e está listada em
 * `api/interno/web/servidor.go`: o base64 engorda 33%, então 25 MB aqui
 * viram ~33,3 MB de corpo, que cabem nos 36 MB da API e nos 40 MB do nginx.
 * Subir aqui sem subir lá faz o envio morrer com 413, sem frase na tela. */
const LIMITE_ANEXOS = 25 * 1024 * 1024;

interface AnexoEscolhido {
  nome: string;
  tipo: string;
  /** base64 puro, sem o prefixo `data:` — é o que o contrato espera. */
  conteudo: string;
  bytes: number;
}

/** Lê o arquivo como base64, sem o prefixo `data:<tipo>;base64,`. */
function lerComoBase64(arquivo: File): Promise<string> {
  return new Promise((resolver, recusar) => {
    const leitor = new FileReader();
    leitor.onerror = () => recusar(leitor.error);
    leitor.onload = () => {
      const texto = String(leitor.result ?? "");
      const virgula = texto.indexOf(",");
      resolver(virgula >= 0 ? texto.slice(virgula + 1) : texto);
    };
    leitor.readAsDataURL(arquivo);
  });
}

export function Escrever({
  inicial,
  aoFechar,
  aoEnviar,
  encaixada,
}: {
  inicial: Rascunho;
  aoFechar: () => void;
  aoEnviar: (aviso: string) => void;
  /**
   * ENCAIXADA NA COLUNA, em vez de flutuando por cima.
   *
   * Apontamento 47 da revisão de UX: "a janela de resposta tampa o texto
   * respondido". É o problema de verdade de uma janela presa ao canto
   * inferior direito — para citar um trecho ou conferir um número, a pessoa
   * tem que minimizar, ler, e abrir de novo.
   *
   * Encaixada, ela é um item de flex no FIM da coluna de leitura: a mensagem
   * fica acima, com a rolagem dela, e a resposta embaixo. É o que o Gmail
   * faz quando se responde de dentro de uma conversa, e foi o ajuste pedido.
   *
   * Quem decide é quem abre (ver correio/Layout): resposta e
   * encaminhamento entram encaixados, porque têm uma mensagem na tela para
   * não cobrir; mensagem NOVA continua flutuante, porque não há nada embaixo
   * dela e uma barra ocupando metade da lista seria pior.
   */
  encaixada?: boolean;
}) {
  const fila = useQueryClient();
  const [para, setPara] = useState(inicial.para ?? "");
  const [copia, setCopia] = useState(inicial.cc ?? "");
  // A linha de cópia aparece sozinha quando já vem preenchida: em "responder
  // a todos", esconder quem vai receber seria esconder o efeito do botão.
  const [mostrarCopia, setMostrarCopia] = useState(!!inicial.cc);
  // CÓPIA OCULTA nunca vem preenchida de origem, e é por desenho: responder
  // a uma mensagem em que VOCÊ estava no Cco não revela os outros ocultos —
  // o servidor de quem enviou nem mandou essa lista. Então ela só aparece
  // quando a pessoa pede.
  const [copiaOculta, setCopiaOculta] = useState("");
  const [mostrarOculta, setMostrarOculta] = useState(false);
  const [assunto, setAssunto] = useState(inicial.assunto ?? "");
  // O CORPO INICIAL VEM COMO TEXTO PURO e tem de virar HTML aqui.
  //
  // `paraResponder` e `paraEncaminhar` montam a citação com `\n` e `>`. Pondo
  // isso direto no `innerHTML`, as quebras de linha somem e a conversa
  // citada vira UM parágrafo só — o histórico fica ilegível, e quem responde
  // só descobre depois de enviar.
  const [corpo, setCorpo] = useState(() => textoParaHTML(inicial.corpo ?? ""));

  // A ASSINATURA, que até 28/10/2026 era salva em /conta e NUNCA inserida.
  //
  // O contrato dizia, com estas palavras, "texto acrescentado ao compor" — e
  // ninguém acrescentava. A pessoa escrevia, salvava, abria uma mensagem
  // nova e não estava lá: indistinguível de "não implementado", que foi
  // exatamente como o Jhou descreveu a tela de conta.
  const { data: assinatura } = useQuery({
    queryKey: ["assinatura"],
    queryFn: conta.assinatura,
    staleTime: 5 * 60 * 1000,
  });
  const assinaturaPosta = useRef(false);

  useEffect(() => {
    const texto = assinatura?.texto?.trim();
    if (!texto || assinaturaPosta.current) return;
    assinaturaPosta.current = true;

    // `-- ` (traço, traço, ESPAÇO) numa linha só é o separador de assinatura
    // da RFC 3676 §4.3. Não é enfeite: é por ele que os clientes de e-mail
    // sabem onde a assinatura começa, para recolhê-la ao citar. Escrever
    // "--" sem o espaço parece igual na tela e perde esse reconhecimento.
    //
    // MAS SÓ QUANDO A PESSOA NÃO PÔS O DELA. Muita gente já escreve a
    // própria linha divisória — a assinatura de laboratório começava com
    // "—" —, e acrescentar a nossa em cima produz DUAS, uma embaixo da
    // outra. Ficou assim na primeira versão, e aparece na tela como defeito.
    //
    // Não mexemos no texto de quem escreveu: ou ele já tem separador e
    // entra como está, ou não tem e ganha o da RFC.
    // UM travessão sozinho já é separador; um hífen sozinho não é.
    // A primeira versão exigia dois ou mais de qualquer um, e a assinatura
    // de laboratório — que começa com "—" — continuou ganhando o nosso em
    // cima. Medido na tela, não deduzido.
    const temSeparador = /^\s*([-_*]{2,}|[–—]+)\s*$/.test(texto.split("\n")[0] ?? "");
    const bloco = textoParaHTML("\n\n" + (temSeparador ? "" : "-- \n") + texto);

    // ANTES da citação, não no fim de tudo. Numa resposta, quem lê quer ver
    // o texto novo e quem assina logo abaixo; a conversa antiga vem depois.
    // Jogar a assinatura para o fim a esconderia atrás de meses de histórico.
    setCorpo((atual) => (atual ? bloco + "<br><br>" + atual : bloco));
    // NÃO marca como sujo: a assinatura sozinha não é motivo para gravar um
    // rascunho. Quem abre e fecha a janela sem escrever nada não deve
    // deixar rastro na pasta.
  }, [assinatura]);
  const [erro, setErro] = useState("");
  const [programando, setProgramando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [minimizada, setMinimizada] = useState(false);
  const [estadoRascunho, setEstadoRascunho] = useState("");
  const [anexos, setAnexos] = useState<AnexoEscolhido[]>([]);
  const [links, setLinks] = useState<correio.ArquivoSubido[]>([]);
  const [subindo, setSubindo] = useState("");
  const [lendoAnexo, setLendoAnexo] = useState(false);
  const campoArquivo = useRef<HTMLInputElement>(null);

  const sujo = useRef(false);
  const salvando = useRef(false);

  // Identificador desta JANELA de escrita, gerado uma vez e enviado em todos
  // os salvamentos automáticos.
  //
  // APPEND não substitui: sem ele, cada salvamento deixava um rascunho novo
  // na pasta, e enviar não apagava nenhum — a mensagem ficava em Enviados E
  // em Rascunhos, aparecendo duas vezes na mesma conversa.
  const rascunhoID = useRef(crypto.randomUUID());

  const montar = useCallback((): MensagemEnvio => {
    return {
      para: separarEnderecos(para),
      cc: mostrarCopia ? separarEnderecos(copia) : undefined,
      cco: mostrarOculta ? separarEnderecos(copiaOculta) : undefined,
      assunto,
      // AS DUAS PARTES, SEMPRE. `corpo` agora é HTML (o editor), e a versão
      // simples é derivada dele. Mandar só o HTML deixaria cliente que lê
      // texto puro com a mensagem VAZIA — e `multipart/alternative` existe
      // exatamente para esse caso.
      corpo_texto: htmlParaTexto(corpo),
      corpo_html: corpo,
      rascunho_id: rascunhoID.current,
      // Só no rascunho: nas rotas de responder e encaminhar a origem já vai
      // no caminho da URL. É o que põe o rascunho DENTRO da conversa.
      origem: inicial.modo === "responder" ? inicial.origem : undefined,
    } as MensagemEnvio;
  }, [para, copia, mostrarCopia, copiaOculta, mostrarOculta, assunto, corpo, inicial.modo, inicial.origem]);

  // RASCUNHO AUTOMÁTICO.
  //
  // Salva o que mudou, e só depois de três segundos parado. Salvar a cada
  // tecla mandaria uma requisição por letra; salvar só ao fechar perderia o
  // texto quando a aba cai — que é exatamente quando o rascunho importa.
  useEffect(() => {
    if (!sujo.current) return;
    const relogio = setTimeout(async () => {
      if (salvando.current) return;
      // VAZIO SE MEDE PELO TEXTO, não pelo HTML: um editor em que ninguém
      // digitou nada costuma conter `<br>` ou `<div><br></div>`, que não são
      // string vazia. Sem isto, abrir e fechar a janela salvaria um rascunho
      // em branco a cada vez.
      if (!para.trim() && !assunto.trim() && !htmlParaTexto(corpo).trim()) return;
      salvando.current = true;
      setEstadoRascunho("salvando…");
      try {
        await correio.salvarRascunho(montar());
        setEstadoRascunho("rascunho salvo");
        void fila.invalidateQueries({ queryKey: ["pastas"] });
      } catch {
        // Falhar aqui não pode interromper quem está escrevendo. O texto
        // continua na tela; o aviso diz que ele ainda não está guardado.
        setEstadoRascunho("não foi possível salvar o rascunho");
      } finally {
        salvando.current = false;
      }
    }, ESPERA_RASCUNHO);
    return () => clearTimeout(relogio);
  }, [para, assunto, corpo, montar, fila]);

  function mudou<T>(definir: (v: T) => void) {
    return (v: T) => {
      sujo.current = true;
      setEstadoRascunho("");
      definir(v);
    };
  }

  const bytesAnexados = anexos.reduce((soma, a) => soma + a.bytes, 0);

  async function escolherArquivos(lista: FileList | null) {
    if (!lista || lista.length === 0) return;
    setErro("");
    setLendoAnexo(true);
    try {
      const novos: AnexoEscolhido[] = [];
      let soma = bytesAnexados;
      for (const arquivo of Array.from(lista)) {
        // ACIMA DO TETO O ARQUIVO VIRA LINK, em vez de ser recusado.
        //
        // Não é só conveniência: acima de 25 MB o anexo NÃO CHEGA. O Postfix
        // aceita 50 MB por mensagem, mas Gmail e Outlook recusam acima de
        // 25–35 MB — então "mandar mesmo assim" seria entregar uma devolução
        // ao remetente, não uma mensagem ao destinatário.
        if (soma > LIMITE_ANEXOS) {
          soma -= arquivo.size;
          try {
            setSubindo(`enviando ${arquivo.name}…`);
            const subido = await correio.subirArquivo(arquivo);
            setLinks((atuais) => [...atuais, subido]);
            sujo.current = true;
          } catch (e) {
            setErro(explicar(e));
            break;
          } finally {
            setSubindo("");
          }
          continue;
        }
        novos.push({
          nome: arquivo.name,
          tipo: arquivo.type || "application/octet-stream",
          conteudo: await lerComoBase64(arquivo),
          bytes: arquivo.size,
        });
      }
      if (novos.length > 0) {
        sujo.current = true;
        setAnexos((atuais) => [...atuais, ...novos]);
      }
    } catch {
      setErro("Não consegui ler o arquivo. Tente de novo.");
    } finally {
      setLendoAnexo(false);
      // Limpa o campo para o MESMO arquivo poder ser escolhido de novo
      // depois de removido — senão o `change` não dispara.
      if (campoArquivo.current) campoArquivo.current.value = "";
    }
  }

  // A MONTAGEM COMPLETA, numa função só.
  //
  // Enviar agora e programar têm de produzir EXATAMENTE a mesma mensagem —
  // mesmos anexos, mesmo rodapé de arquivos grandes, mesmo corpo nas duas
  // partes. Duas cópias desta montagem seria duas chances de divergirem, e a
  // que diverge é sempre a menos usada: a programada, que ninguém confere na
  // hora porque ela só sai amanhã.
  function montarCompleto(): MensagemEnvio {
    // OS LINKS VÃO NO CORPO, não em cabeçalho nem em anexo: é texto que
    // quem recebe precisa ver e poder clicar em qualquer programa de
    // e-mail, inclusive os que não entendem HTML.
    const rodape =
      links.length === 0
        ? ""
        : "\n\n---\nArquivos grandes demais para anexar:\n" +
          links
            .map(
              (l) =>
                `${l.nome} (${tamanho(l.bytes)})\n${l.url}\n` +
                `O link expira em ${new Date(l.expira_em).toLocaleDateString("pt-BR")}.`,
            )
            .join("\n\n");

    const base = montar();
    // O rodapé dos arquivos grandes vai nas DUAS partes. Só no texto, ele
    // sumiria para quem lê a versão HTML — que é a maioria — e os links
    // que a pessoa acabou de anexar não apareceriam para ninguém.
    return {
      ...base,
      corpo_texto: (base.corpo_texto ?? "") + rodape,
      corpo_html: rodape
        ? (base.corpo_html ?? "") + "<br><br>" + textoParaHTML(rodape.replace(/^\n+/, ""))
        : base.corpo_html,
      // OS ANEXOS ENTRAM SÓ AQUI, nunca no `montar()`. O `montar()` também
      // alimenta o rascunho automático, que salva a cada três segundos
      // parado — com o anexo dentro, cada pausa de digitação subiria 25 MB
      // de base64 de novo.
      anexos:
        anexos.length > 0
          ? anexos.map(({ nome, tipo, conteudo }) => ({ nome, tipo, conteudo }))
          : undefined,
    } as MensagemEnvio;
  }

  // PROGRAMAR usa a mesma montagem e o mesmo tratamento de erro do envio.
  async function programar(quando: Date) {
    setErro("");
    setProgramando(false);
    if (!para.trim()) {
      setErro("Informe ao menos um destinatário.");
      return;
    }
    setEnviando(true);
    try {
      await correio.agendarEnvio(
        montarCompleto(),
        quando,
        inicial.modo === "responder" || inicial.modo === "encaminhar" ? inicial.modo : undefined,
        inicial.origem,
      );
      void fila.invalidateQueries({ queryKey: ["agendados"] });
      aoEnviar(
        "Programada para " +
          new Intl.DateTimeFormat("pt-BR", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
          }).format(quando) + ".",
      );
      aoFechar();
    } catch (e) {
      setErro(explicar(e));
    } finally {
      setEnviando(false);
    }
  }

  async function enviar() {
    setErro("");
    if (!para.trim()) {
      setErro("Informe ao menos um destinatário.");
      return;
    }
    setEnviando(true);
    try {
      const envio = montarCompleto();
      if (inicial.modo === "responder" && inicial.origem) {
        await correio.responder(inicial.origem, envio);
      } else if (inicial.modo === "encaminhar" && inicial.origem) {
        await correio.encaminhar(inicial.origem, envio);
      } else {
        await correio.enviar(envio);
      }
      void fila.invalidateQueries({ queryKey: ["mensagens"] });
      void fila.invalidateQueries({ queryKey: ["pastas"] });
      aoEnviar("Mensagem enviada.");
      aoFechar();
    } catch (e) {
      setErro(explicar(e));
    } finally {
      setEnviando(false);
    }
  }

  if (minimizada) {
    return (
      <div className="fixed bottom-0 inset-x-0 sm:inset-x-auto sm:right-6 sm:w-80 rounded-t-lg bg-tinta text-white shadow-2xl z-40">
        <div className="flex items-center gap-2 px-4 py-2.5 text-sm">
          <span className="flex-1 truncate">{assunto || "Nova mensagem"}</span>
          <button onClick={() => setMinimizada(false)} aria-label="Restaurar">
            <Icone nome="Maximize2" tamanho={14} />
          </button>
          <button onClick={aoFechar} aria-label="Descartar">
            <Icone nome="X" tamanho={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    // NO CELULAR ela ocupa a tela inteira, e não é preferência: uma janela de
    // 540px presa ao canto direito num aparelho de 380px deixa o campo de
    // texto com uma coluna de palavra por linha. `inset-0` no celular,
    // janela flutuante do `sm` para cima, como no mockup.
    //
    // `h-[100dvh]` e não `h-screen`: no celular a `vh` inclui a barra do
    // navegador que some ao rolar, e o botão Enviar ficava embaixo dela —
    // escrevia-se a mensagem e não dava para enviar.
    // A ALTURA É DEFINIDA NO DESKTOP, e não `h-auto`.
    //
    // Com `sm:h-auto` a janela encolhia até o conteúdo, e aí o `flex-1` do
    // editor não tinha o que preencher: a área de escrever ficava com uns
    // noventa pixels — cabia uma linha e meia. Altura automática só funciona
    // quando o conteúdo pede espaço; aqui é o contrário, o espaço é que
    // convida a escrever.
    //
    // 640px: medido contra o Gmail, onde o CORPO ocupa mais da metade da
    // janela. Com 560 a área de escrever ficava com ~336px e a proporção
    // ainda era de formulário, não de lugar para escrever.
    //
    // O `max-h` existe para telas baixas: sem ele
    // a janela passaria da altura disponível e o botão Enviar ficaria fora
    // do alcance, que é pior que ser pequena.
    <div
      className={
        encaixada
          ? // ENCAIXADA: item de flex no fim da coluna, com a mensagem acima.
            //
            // `h-[55%]` E NÃO `h-auto`, e isto é o aprendizado que já está
            // escrito algumas linhas abaixo, sobre a janela flutuante: com
            // altura automática a caixa encolhe até o conteúdo, e aí o
            // `flex-1` do editor não tem o que preencher — a área de
            // escrever fica com uns noventa pixels. Porcentagem resolve
            // contra a altura da coluna, que é definida, então o editor
            // volta a ter espaço.
            //
            // `min-h-[280px]` para coluna baixa, e `flex-shrink-0` para a
            // resposta não ser comprimida pela mensagem acima — é a
            // resposta que está sendo escrita agora.
            //
            // No CELULAR continua em tela cheia: encaixar numa coluna de
            // 380px daria um terço de tela para a mensagem e um terço para a
            // resposta, e nenhuma das duas legível.
            "fixed inset-0 h-[100dvh] z-40 flex flex-col bg-white sm:static sm:inset-auto sm:z-auto sm:h-[55%] sm:min-h-[280px] sm:flex-shrink-0 sm:border-t sm:shadow-[0_-4px_12px_rgba(0,0,0,0.06)] border-linha"
          : "fixed inset-0 h-[100dvh] sm:inset-auto sm:bottom-0 sm:right-6 sm:h-[640px] sm:max-h-[calc(100vh-2rem)] sm:w-[540px] sm:max-w-[calc(100vw-2rem)] sm:rounded-t-lg bg-white shadow-2xl z-40 flex flex-col sm:border border-linha"
      }
    >
      <div className="flex items-center gap-2 sm:rounded-t-lg bg-tinta px-4 py-2.5 text-sm text-white">
        <span className="flex-1 truncate">{assunto || "Nova mensagem"}</span>
        {/* Minimizar é gesto de janela. Em tela cheia não há para onde
            minimizar, e o botão só confundiria — fechar já guarda rascunho. */}
        <button
          onClick={() => setMinimizada(true)}
          aria-label="Minimizar"
          className="hidden sm:block"
        >
          <Icone nome="Minus" tamanho={16} />
        </button>
        <button onClick={aoFechar} aria-label="Descartar">
          <Icone nome="X" tamanho={16} />
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-linha px-4 py-2">
        <label htmlFor="para" className="text-sm text-tinta-fraca w-12">
          Para
        </label>
        <CampoDeEnderecos
          id="para"
          valor={para}
          aoMudar={mudou(setPara)}
          autoFocus={!inicial.para}
          className="w-full text-base sm:text-sm outline-none"
        />
        {!mostrarCopia && (
          <button
            onClick={() => setMostrarCopia(true)}
            className="shrink-0 text-xs text-tinta-fraca hover:text-acao"
          >
            Cc
          </button>
        )}
        {!mostrarOculta && (
          <button
            onClick={() => setMostrarOculta(true)}
            title="Cópia oculta: quem recebe não vê esta lista"
            className="shrink-0 text-xs text-tinta-fraca hover:text-acao"
          >
            Cco
          </button>
        )}
      </div>

      {mostrarCopia && (
        <div className="flex items-center gap-2 border-b border-linha px-4 py-2">
          <label htmlFor="cc" className="text-sm text-tinta-fraca w-12">
            Cc
          </label>
          {/* O CURSOR VAI PARA O CAMPO QUE ACABOU DE ABRIR.
              Sem isto, clicar em "Cc" revelava a linha e deixava o foco onde
              estava: a pessoa digitava e o texto caía no campo anterior.
              `autoFocus` basta porque esta linha só é MONTADA no clique.

              A exceção é o rascunho que já vem com cópia preenchida (uma
              resposta a todos, por exemplo): aí a linha nasce junto com a
              janela e roubar o foco do assunto seria pior. */}
          <CampoDeEnderecos
            id="cc"
            valor={copia}
            aoMudar={mudou(setCopia)}
            autoFocus={!inicial.cc}
            className="w-full text-base sm:text-sm outline-none"
          />
        </div>
      )}

      {/* CÓPIA OCULTA. O rótulo diz o que ela faz, não só como se chama:
          "Cco" é jargão, e quem erra isso manda a lista de clientes de um
          para o outro. O aviso ao lado é curto de propósito — explicação
          longa em campo de formulário não é lida. */}
      {mostrarOculta && (
        <div className="flex items-center gap-2 border-b border-linha px-4 py-2">
          <label htmlFor="cco" className="w-12 shrink-0 text-sm text-tinta-fraca">
            Cco
          </label>
          <CampoDeEnderecos
            id="cco"
            valor={copiaOculta}
            aoMudar={mudou(setCopiaOculta)}
            autoFocus
            className="w-full text-base sm:text-sm outline-none"
          />
          <span className="shrink-0 text-[11px] text-tinta-fraca">
            ninguém vê esta lista
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-linha px-4 py-2">
        <label htmlFor="assunto" className="sr-only">
          Assunto
        </label>
        <input
          id="assunto"
          value={assunto}
          onChange={(e) => mudou(setAssunto)(e.target.value)}
          placeholder="Assunto"
          autoFocus={inicial.modo === "novo" && !!inicial.para}
          className="min-w-0 flex-1 text-base sm:text-sm outline-none"
        />
      </div>

      <Editor html={corpo} aoMudar={mudou(setCorpo)} />

      {/* OS ARQUIVOS QUE VIRARAM LINK. Separados dos anexos de propósito:
          eles NÃO viajam dentro da mensagem, e quem escreve precisa saber
          disso — o destinatário vai depender de um endereço que vence. */}
      {links.length > 0 && (
        <ul className="flex flex-col gap-1 border-t border-linha px-4 py-2">
          {links.map((l) => (
            <li key={l.uuid} className="flex items-center gap-2 text-xs">
              <Icone nome="Link2" tamanho={12} className="shrink-0 text-acao" />
              <span className="min-w-0 truncate text-tinta" title={l.nome}>
                {l.nome}
              </span>
              <span className="shrink-0 tabular-nums text-tinta-fraca">
                {tamanho(l.bytes)}
              </span>
              <span className="shrink-0 text-tinta-fraca">
                · link até {new Date(l.expira_em).toLocaleDateString("pt-BR")}
              </span>
              <button
                type="button"
                onClick={() => setLinks((atuais) => atuais.filter((x) => x.uuid !== l.uuid))}
                aria-label={`Tirar ${l.nome} da mensagem`}
                className="ml-auto shrink-0 rounded-full p-1 text-tinta-fraca hover:bg-linha hover:text-perigo"
              >
                <Icone nome="X" tamanho={12} />
              </button>
            </li>
          ))}
          <li className="text-[11px] text-tinta-fraca">
            Grande demais para anexar — vai como endereço no fim da mensagem.
          </li>
        </ul>
      )}

      {/* OS ANEXOS ESCOLHIDOS, acima do rodapé: quem anexou precisa VER o que
          vai junto antes de clicar em enviar. Anexo que some da tela é anexo
          que a pessoa manda sem querer — ou que ela acha que mandou. */}
      {anexos.length > 0 && (
        <ul className="flex flex-wrap gap-2 border-t border-linha px-4 py-2">
          {anexos.map((a, i) => (
            <li
              key={`${a.nome}-${i}`}
              className="flex max-w-full items-center gap-2 rounded-full border border-linha bg-fundo py-1 pl-3 pr-1 text-xs"
            >
              <Icone nome="Paperclip" tamanho={12} className="shrink-0 text-tinta-fraca" />
              <span className="min-w-0 truncate text-tinta" title={a.nome}>
                {a.nome}
              </span>
              <span className="shrink-0 tabular-nums text-tinta-fraca">{tamanho(a.bytes)}</span>
              <button
                type="button"
                onClick={() => setAnexos((atuais) => atuais.filter((_, j) => j !== i))}
                aria-label={`Remover ${a.nome}`}
                className="shrink-0 rounded-full p-1 text-tinta-fraca hover:bg-linha hover:text-perigo"
              >
                <Icone nome="X" tamanho={12} />
              </button>
            </li>
          ))}
          <li className="flex items-center text-xs text-tinta-fraca">
            {tamanho(bytesAnexados)} de {tamanho(LIMITE_ANEXOS)} · não ficam no rascunho
          </li>
        </ul>
      )}

      {erro && <p className="px-4 pb-2 text-sm text-perigo">{erro}</p>}

      <div className="flex items-center gap-3 border-t border-linha px-4 py-3">
        {/* ENVIAR E PROGRAMAR DIVIDEM UM BOTÃO, como no Gmail: a seta abre os
            horários sem tirar "Enviar" do caminho de quem só quer enviar
            agora, que é a esmagadora maioria. */}
        <div className="relative flex">
          <button
            onClick={() => void enviar()}
            disabled={enviando}
            className="rounded-l-full bg-acao px-6 py-2 text-sm text-white disabled:opacity-60"
          >
            {enviando ? "Enviando…" : "Enviar"}
          </button>
          <button
            type="button"
            onClick={() => setProgramando((v) => !v)}
            disabled={enviando}
            aria-label="Programar envio"
            aria-expanded={programando}
            title="Programar envio"
            className="rounded-r-full border-l border-white/25 bg-acao px-2.5 py-2 text-white disabled:opacity-60"
          >
            <Icone nome="ChevronDown" tamanho={16} />
          </button>
          {programando && (
            <ProgramarEnvio
              aoFechar={() => setProgramando(false)}
              aoEscolher={(quando) => void programar(quando)}
            />
          )}
        </div>

        {/* O campo de arquivo fica escondido e o botão o aciona: o controle
            nativo não aceita estilo, e um `Escolher arquivo` cinza no meio de
            uma janela desenhada é o que faz a tela parecer inacabada. */}
        <input
          ref={campoArquivo}
          type="file"
          multiple
          onChange={(e) => void escolherArquivos(e.target.files)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => campoArquivo.current?.click()}
          disabled={lendoAnexo || enviando || subindo !== ""}
          title={`Anexar arquivo (até ${tamanho(LIMITE_ANEXOS)} no total)`}
          aria-label="Anexar arquivo"
          className="rounded-full p-2 text-tinta-media hover:bg-linha disabled:opacity-40"
        >
          <Icone nome="Paperclip" tamanho={18} />
        </button>

        <span className="text-xs text-tinta-fraca" aria-live="polite">
          {subindo || (lendoAnexo ? "lendo o arquivo…" : estadoRascunho)}
        </span>
      </div>
    </div>
  );
}
